import * as queryString from 'query-string';
import * as React from 'react';
import * as _ from 'underscore';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { AngularDirective } from '../../../common/react/components/AngularDirective';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { IVizScope } from '../../../ts/directives/tbViz';
import { AngularContext } from '../../../ts/factories/AngularContextFactory';
import { IViz, VizType } from '../../../ts/models/Viz';
import { setVizPath } from '../../actions/spaces/SetVizPathAction';
import { VizPathInitialState } from '../../reducers/VizPathReducer';
import { selectVizPath } from '../../selectors/VizPathSelector';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';
import { SpacesSiteUrls } from '../SpacesSiteUrls';

export const SiteAdminAnalysisVizPathPrefix = 'tabbed_site_views/';
export const ServerAdminAnalysisVizPathPrefix = 'tabbed_admin_views/';

export interface AdminAnalysisVizSpaceProviderOwnProps extends ReactDirectiveProps {
  readonly sheetUrlId: string;
  readonly routeLocationSearch: string;
  readonly urls: SpacesSiteUrls;
  readonly isServerAnalysis: boolean;
  readonly renderSpace: (angularContext: AngularContext, setupScope: (scope: ng.IScope) => ng.IScope) => JSX.Element;
}

export interface AdminAnalysisVizSpaceProviderStateProps {
  readonly vizPath: string;
}

export interface AdminAnalysisVizSpaceProviderDispatchProps {
  readonly setVizPath: (vizPath: string) => void;
}

const template = `<div ng-include="'adminVizViewer.html'"></div>`;

export const renderAdminAnalysisVizSpace = (angularContext: AngularContext, setupScope: (scope: ng.IScope) => ng.IScope): JSX.Element => {
  // the parent div here is important to ensure AngularDirective plays nice when unmounting
  return (
    <div>
      <AngularDirective
        template={template}
        angularContext={angularContext}
        setupScope={setupScope}
      />
    </div>
  );
};

export type AdminAnalysisVizSpaceProviderProps = AdminAnalysisVizSpaceProviderOwnProps & AdminAnalysisVizSpaceProviderStateProps & AdminAnalysisVizSpaceProviderDispatchProps;

export class AdminAnalysisVizSpaceProvider extends React.Component<AdminAnalysisVizSpaceProviderProps> {

  private vizPathPrefix: string = undefined;
  private spaceScope: IVizScope = null;


  public constructor(props: AdminAnalysisVizSpaceProviderProps) {
    super(props);
    this.state = {
      hasError: false
    };

    this.vizPathPrefix = this.props.isServerAnalysis? ServerAdminAnalysisVizPathPrefix : SiteAdminAnalysisVizPathPrefix;
  }

  public render() {
    return this.props.renderSpace(this.props.angularContext, this.setupScope);
  }

  public componentDidMount() {
    // initial viz path for analysis vizzes
    this.props.setVizPath(this.vizPathPrefix + this.props.sheetUrlId);
  }

  public componentDidUpdate(prevProps: AdminAnalysisVizSpaceProviderProps) {
    const vizPathFromSheetUrlId = this.vizPathPrefix + this.props.sheetUrlId;
    if (prevProps.sheetUrlId !== this.props.sheetUrlId && vizPathFromSheetUrlId !== this.props.vizPath) {
      // the url sheetId changed (because the sheetUrlId is different on the props)
      // but the vizPath is not updated yet. So update it and reload the viz.
      this.props.setVizPath(vizPathFromSheetUrlId);

      if (this.spaceScope) {
        const previousViz: IViz = this.spaceScope.viz;

        this.spaceScope.viz = {
          path: vizPathFromSheetUrlId,
          vizType: VizType.Admin,
          params:  queryString.parse(this.props.routeLocationSearch || ''),
          allowReloadViz: true,  // this needs to be true so we can reload the viz when the url changes

          reloadViz: previousViz.reloadViz,
          isExplicitOriginalViz: previousViz.isExplicitOriginalViz,
          instanceId: previousViz.instanceId,
        };
        this.spaceScope.$apply();
        this.spaceScope.viz.reloadViz && this.spaceScope.viz.reloadViz();
      }
    }
  }

  public shouldComponentUpdate(nextProps: AdminAnalysisVizSpaceProviderProps) {
    if(this.props.isServerAnalysis !== nextProps.isServerAnalysis) {
      return true;
    }

    if (nextProps.vizPath !== VizPathInitialState &&
        nextProps.vizPath !== this.props.vizPath &&
        nextProps.vizPath !== (this.vizPathPrefix + nextProps.sheetUrlId)) {
          // vizPath changed via user clicking on viz tab (that's why the url does not match the new vizPath-- see tbViz),
          // so update the url and don't rerender
          return false;
    }

    return true;
  }

  private setupScope = (scope: ng.IScope) => {
    const childScope = scope.$new() as IVizScope;

    childScope.viz = {
      path: this.vizPathPrefix + this.props.sheetUrlId,
      vizType: VizType.Admin,
      params:  queryString.parse(this.props.routeLocationSearch || ''),
      allowReloadViz: true  // this needs to be true so we can reload the viz when the url changes
    };

    this.spaceScope = childScope;
    return childScope;
  }
}

const mapStateToProps: MapStateToProps<AdminAnalysisVizSpaceProviderStateProps> = (appState) => ({
  vizPath: selectVizPath(appState)
});

const mapDispatchToProps: MapDispatchToProps<AdminAnalysisVizSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  setVizPath
}, dispatch);

export const ConnectedAdminAnalysisVizSpaceProvider: React.ComponentClass<AdminAnalysisVizSpaceProviderOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(AdminAnalysisVizSpaceProvider);
